import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase, advanceWorkflow } from '../../services/workQueue';
import { useToast } from '../../providers/ToastProvider';
import { reportError } from '../../services/errorReporting';
import { invalidateCaseDetail } from './util';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const MAX_LEN = 10_000;

interface SubmitForManufacturingConfirmModalProps {
    workCase: WorkQueueCase;
    onClose: () => void;
    onSuccess: () => void;
    /** Engine action to fire. Defaults to the initial-case submit action. */
    action?: string;
}

export function SubmitForManufacturingConfirmModal({
    workCase: c,
    onClose,
    onSuccess,
    action = 'submit-for-manufacturing'
}: SubmitForManufacturingConfirmModalProps) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [postApprovalText, setPostApprovalText] = useState('');

    useEscapeKey(onClose, !submitting);

    const isRedesignDecision = action === 'submit-post-approval-redesign';
    const trimmedChanges = postApprovalText.trim();
    const overLimit = trimmedChanges.length > MAX_LEN;

    const handleConfirm = async (): Promise<void> => {
        setSubmitting(true);
        try {
            if (isRedesignDecision && trimmedChanges.length > 0) {
                await advanceWorkflow(c.id, 'request-post-approval-redesign', {
                    designPlan: trimmedChanges
                });
                await invalidateCaseDetail(queryClient, c.id);
                showToast(`Sent for post-approval redesign: ${c.name}`, 'success');
            } else {
                await advanceWorkflow(c.id, action, {});
                await invalidateCaseDetail(queryClient, c.id);
                showToast(
                    isRedesignDecision
                        ? `Proceeding to SDI creation: ${c.name}`
                        : `Case submitted for manufacturing: ${c.name}`,
                    'success'
                );
            }
            onSuccess();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            showToast('Failed to submit for manufacturing. Please try again.', 'error');
            reportError({
                source: 'SubmitForManufacturingConfirmModal.handleConfirm',
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id, action }
            });
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-35 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl w-[480px] shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        <span className="font-bold text-[15px] text-gray-800">
                            {isRedesignDecision ? 'Submit redesign' : 'Mark as submitted'}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <p className="text-sm text-gray-700">
                        {isRedesignDecision
                            ? 'Add post-approval changes to send the case for redesign, or leave blank to proceed straight to SDI creation.'
                            : 'Confirm that this case has been prepared and submitted to the manufacturer. This will advance the case to the next manufacturing step.'}
                    </p>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
                        <div className="flex items-baseline gap-2">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                Patient
                            </span>
                            <span className="text-sm font-bold text-gray-800 truncate">
                                {c.name}
                            </span>
                        </div>
                        <div className="mt-1 flex items-baseline gap-2">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                Practice
                            </span>
                            <span className="text-xs text-gray-600 truncate">{c.practice}</span>
                        </div>
                    </div>

                    {isRedesignDecision && (
                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-700">
                                    Post-approval changes for the manufacturer{' '}
                                    <span className="font-normal text-gray-400">(optional)</span>
                                </label>
                                <span
                                    className={`text-[11px] tabular-nums ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
                                >
                                    {trimmedChanges.length.toLocaleString()} /{' '}
                                    {MAX_LEN.toLocaleString()}
                                </span>
                            </div>
                            <textarea
                                value={postApprovalText}
                                onChange={(e) => setPostApprovalText(e.target.value)}
                                placeholder="Leave blank to proceed straight to SDI. Filling this field sends the case for redesign first."
                                rows={4}
                                disabled={submitting}
                                spellCheck
                                className={`w-full px-3 py-2 border rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none leading-relaxed disabled:opacity-50 ${
                                    overLimit
                                        ? 'border-red-300 focus:border-red-400'
                                        : trimmedChanges.length > 0
                                          ? 'border-green-500 bg-green-50/40 focus:border-green-600'
                                          : 'border-gray-300 focus:border-[rgb(55,115,168)]'
                                }`}
                            />
                            {trimmedChanges.length > 0 && !overLimit && (
                                <p className="text-[11px] text-amber-600">
                                    Filling this field will send the case for redesign instead of
                                    directly to Create SDI.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-bold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            void handleConfirm();
                        }}
                        disabled={submitting || overLimit}
                        className="px-4 py-2 rounded-md bg-gray-800 text-white text-xs font-bold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Submitting…' : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}
